import * as THREE from 'three';
import { onCameraMoveCallback, onCameraZoomCallback, ViewerScreenDirection } from '../Types';
import { ThreeSixtyViewer } from '../ThreeSixtyViewer';
import { Switch } from '@mobiscroll/angular-lite/src/js/classes/switch';
import Vector3 = THREE.Vector3;
import { Utilities } from '../Utilities';
import { raycaster } from 'three-viewport-gizmo';
import { animate } from '@angular/animations';

export class CameraController {
  set defaultAngle(value: { horizontalAngle: number; verticalAngle: number }) {
    this._defaultAngle = value;
  }
  private camera: THREE.PerspectiveCamera;
  private target: THREE.Vector3 = new THREE.Vector3();
  private screenDirection: ViewerScreenDirection;
  private isUserInteracting: boolean = false;
  private onMouseDownMouseX: number = 0;
  private onMouseDownMouseY: number = 0;
  private lon: number = 0;
  private lat: number = 0;
  private onMouseDownLon: number = 0;
  private onMouseDownLat: number = 0;
  private phi: number = 0;
  private theta: number = 0;
  private readonly minZoomFov: number = 10; // Lower limit for zoom - decreased from 20 to allow more zoom
  private readonly maxZoomFov: number = 115; // Upper limit for zoom
  onCameraMoveListeners: onCameraMoveCallback[] = [];
  onCameraZoomListeners: onCameraZoomCallback[] = [];
  private _defaultAngle = { horizontalAngle: 0, verticalAngle: 0 };

  // Debounced zoom handling
  private zoomStoppedCallbacks: (() => void)[] = [];
  private zoomDebounceTimeout?: number;
  private readonly ZOOM_DEBOUNCE_DELAY = 300; // Wait 300ms after last wheel event

  private scene: THREE.Scene;
  private initialCameraRotation: number;

  constructor(
    camera: THREE.PerspectiveCamera,
    scene: THREE.Scene,
    screenDirection: ViewerScreenDirection,
  ) {
    this.camera = camera;
    this.scene = scene;
    this.screenDirection = screenDirection;
  }

  private emitCameraMove(): void {
    this.onCameraMoveListeners.forEach((listener) => listener(this.getCameraTarget()));
  }
  private emitCameraZoom(zoomVlue: number): void {
    this.onCameraZoomListeners.forEach((listener) => listener({ zoomValue: zoomVlue }));
  }
  public onMouseDown(event: MouseEvent): void {
    event.preventDefault();

    let mouse: THREE.Vector2 = new THREE.Vector2();
    if (ThreeSixtyViewer.isLocked === true) {
      if (
        event.clientX < window.innerWidth / 2 &&
        this.screenDirection === ViewerScreenDirection.LEFT
      ) {
        mouse.x = (event.clientX / (window.innerWidth / 2)) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      } else if (
        event.clientX > window.innerWidth / 2 &&
        this.screenDirection === ViewerScreenDirection.RIGHT
      ) {
        mouse.x = ((event.clientX - window.innerWidth / 2) / (window.innerWidth / 2)) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      } else return;
    } else {
      mouse.x = (event.clientX / (window.innerWidth / 2)) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    }
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.camera);
    const intersects = raycaster.intersectObjects(this.scene.children, false);

    if (intersects.length > 0) {
      this.isUserInteracting = true;
    }

    // Save the initial mouse position and camera rotation
    this.onMouseDownMouseX = event.clientX;
    this.onMouseDownMouseY = event.clientY;
    this.onMouseDownLon = this.lon;
    this.onMouseDownLat = this.lat;
    this.initialCameraRotation = this.lon;
  }

  public onMouseMove(event: MouseEvent): void {
    if (this.isUserInteracting) {
      // Calculate zoom-dependent sensitivity
      // When FOV is low (zoomed in), sensitivity should be low for precise control
      // When FOV is high (zoomed out), sensitivity should be higher for faster navigation
      const zoomFactor = this.camera.fov / this.maxZoomFov; // Normalized zoom factor (0 to 1)
      const baseSensitivity = 0.1;
      const zoomSensitivity = baseSensitivity * zoomFactor;

      // Calculate mouse movement deltas with zoom-dependent sensitivity
      const deltaLon = (event.clientX - this.onMouseDownMouseX) * zoomSensitivity; // Horizontal movement
      const deltaLat = (event.clientY - this.onMouseDownMouseY) * zoomSensitivity; // Vertical movement

      // Reverse horizontal movement by subtracting deltaLon instead of adding it
      this.lon = this.onMouseDownLon - deltaLon;
      this.lat = this.onMouseDownLat + deltaLat;
      this.setCameraTarget();

      this.emitCameraMove();
    }
  }

  public onMouseUp(): void {
    this.isUserInteracting = false;
  }

  private currentAnimationFrame: number | null = null;

  private throttleTimeout: number | null = null;

  public onMouseWheel(event: WheelEvent): void {
    // Prevent default browser zoom behavior (especially for Windows touchpad pinch-to-zoom)
    event.preventDefault();

    if (this.throttleTimeout !== null) {
      return; // Skip this event if we're still in the throttle period
    }

    const targetFov = this.camera.fov + event.deltaY * 0.1;
    const clampedFov = Math.max(this.minZoomFov, Math.min(this.maxZoomFov, targetFov));
    this.lerpZoom(clampedFov);
    
    // Set throttle timeout (e.g., 100ms)
    this.throttleTimeout = window.setTimeout(() => {
      this.throttleTimeout = null;
    }, 100);

    // Handle debounced zoom stop detection
    this.handleZoomDebounce();
  }

  /**
   * Handle debounced zoom stop detection
   */
  private handleZoomDebounce(): void {
    // Clear existing timeout
    if (this.zoomDebounceTimeout) {
      clearTimeout(this.zoomDebounceTimeout);
    }

    // Set new timeout
    this.zoomDebounceTimeout = window.setTimeout(() => {
      this.zoomDebounceTimeout = undefined;
      this.notifyZoomStopped();
    }, this.ZOOM_DEBOUNCE_DELAY);
  }

  /**
   * Notify all zoom stopped callbacks
   */
  private notifyZoomStopped(): void {
    this.zoomStoppedCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        // Silently handle callback errors
      }
    });
  }

  /**
   * Add callback for when zoom stops (debounced)
   */
  public addZoomStoppedCallback(callback: () => void): void {
    this.zoomStoppedCallbacks.push(callback);
  }

  /**
   * Remove zoom stopped callback
   */
  public removeZoomStoppedCallback(callback: () => void): void {
    const index = this.zoomStoppedCallbacks.indexOf(callback);
    if (index >= 0) {
      this.zoomStoppedCallbacks.splice(index, 1);
    }
  }

  private lerpZoom(targetFov: number): void {
    const duration = 200; // Duration of the lerp in milliseconds
    const startFov = this.camera.fov;
    const startTime = performance.now();

    const animate = (time: number) => {
      const elapsed = time - startTime;
      const t = Math.min(elapsed / duration, 1); // Normalized time [0, 1]
      this.camera.fov = THREE.MathUtils.lerp(startFov, targetFov, t);
      this.emitCameraZoom(this.camera.fov);

      if (t < 1) {
        this.currentAnimationFrame = requestAnimationFrame(animate);
      } else {
        this.currentAnimationFrame = null;
      }
      this.camera.updateProjectionMatrix();
    };

    this.currentAnimationFrame = requestAnimationFrame(animate);
  }
  public getCameraTarget() {
    return { lat: this.lat, lon: this.lon, phi: this.phi, theta: this.theta };
  }
  /**
   * Sets the camera's target based on current lat/lon.
   */
  setCameraTarget(
    target: { lat: number; lon: number; phi: number; theta: number } | null = null,
  ): void {
    const radius = 500;

    if (target) {
      this.lat = target.lat;
      this.lon = target.lon;
      this.phi = target.phi;
      this.theta = target.theta;
    } else {
      this.lat = THREE.MathUtils.clamp(this.lat, -85, 85);
      // Note: The formula here assumes lat is inverted relative to KRpano.
      this.phi = THREE.MathUtils.degToRad(90 - this.lat);
      this.theta = THREE.MathUtils.degToRad(this.lon + 180);
    }
    // Removed the negative sign in target.x for correct horizontal orientation.
    this.target.x = radius * Math.sin(this.phi) * Math.cos(this.theta);
    this.target.y = radius * Math.cos(this.phi);
    this.target.z = radius * Math.sin(this.phi) * Math.sin(this.theta);
    this.camera.lookAt(this.target);
  }

  public getCameraOrientation(): { horizontalAngle: number; verticalAngle: number } {
    return {
      horizontalAngle: this.lon,
      verticalAngle: -this.lat,
    };
  }

  public setInitialCameraOrientation(horizontalAngle: number, verticalAngle: number): void {
    // Invert verticalAngle to match Three.js spherical coordinate convention.
    this.lat = -verticalAngle;
    this.lon = horizontalAngle;
    this._defaultAngle = { horizontalAngle: horizontalAngle, verticalAngle: verticalAngle };
    this.setCameraTarget();
    this.emitCameraMove();
  }
  public setCameraZoom(fov: number): void {
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();
  }
  public getCameraZoom(): number {
    return this.camera.fov;
  }
  public resetCameraOrientation(): void {
    this.setInitialCameraOrientation(
      this._defaultAngle.horizontalAngle,
      this._defaultAngle.verticalAngle,
    );
  }
  /**
   * Updates the aspect ratio and projection matrix for the camera.
   */
  public resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }
}
